'use client';

import { useState, useEffect } from 'react';
import { Shield, X, ExternalLink } from 'lucide-react';
import { trackEvent } from './analytics';

const STORAGE_KEY = 'dreambot_privacy_accepted';

export function hasAcceptedPrivacy(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function acceptPrivacy(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Silently fail
  }
}

export function resetPrivacy(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail
  }
}

export interface PrivacyBannerProps {
  onAccept: () => void;
  onDecline?: () => void;
}

export function PrivacyBanner({ onAccept, onDecline }: PrivacyBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!hasAcceptedPrivacy()) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        trackEvent('privacy_banner_shown');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setIsExiting(true);
    acceptPrivacy();
    trackEvent('privacy_accepted');
    setTimeout(() => {
      onAccept();
    }, 300);
  };

  const handleDecline = () => {
    setIsExiting(true);
    trackEvent('privacy_declined');
    setTimeout(() => {
      onDecline?.();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed bottom-4 left-4 right-4 z-[2147483647]
        sm:left-auto sm:right-4 sm:max-w-md
        transition-all duration-300
        ${isExiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}
      `}
    >
      <div className="bg-card rounded-xl shadow-floating p-4 border border-foreground/10">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Shield size={20} className="text-blue-600 dark:text-blue-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Your Privacy Matters
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              We use localStorage to save your conversation and preferences locally on your device. 
              No personal data is shared with third parties. Your data stays on your device.
            </p>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleAccept}
                className="px-4 py-2 bg-foreground text-background rounded-lg text-xs font-semibold interactive-scale hover:opacity-90"
              >
                Accept
              </button>
              <button
                onClick={handleDecline}
                className="px-4 py-2 bg-muted text-foreground rounded-lg text-xs font-semibold interactive-scale hover:opacity-90"
              >
                Decline
              </button>
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => trackEvent('privacy_policy_clicked')}
              >
                Privacy Policy
                <ExternalLink size={12} />
              </a>
            </div>
          </div>

          <button
            onClick={handleDecline}
            aria-label="Close privacy banner"
            className="shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors interactive-scale"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
